/**
 * Forgot Password — request a password reset link by email.
 *
 * Always shows the same generic confirmation regardless of whether the
 * email exists. This matches the backend's privacy behavior (no
 * account-enumeration vector) and keeps the UX consistent.
 */

import React, { useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

import { requestPasswordReset } from "../api/client";
import { toUserSafeErrorMessage } from "../utils/errorMessage";

export default function ForgotPasswordPage() {
  const { t } = useTranslation("pages-products");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitInFlightRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;
    setError(null);
    if (!email) {
      setError(t("ForgotPasswordPage.emailRequiredError"));
      return;
    }
    // State updates are asynchronous. This ref prevents a second request in
    // the interval before the disabled button is rendered.
    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } catch (err: unknown) {
      // The endpoint is generic by design — only network/5xx errors land here.
      setError(
        toUserSafeErrorMessage(
          err,
          t("ForgotPasswordPage.genericErrorMessage"),
        ),
      );
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" component="h1" fontWeight={500} gutterBottom>
            {t("ForgotPasswordPage.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("ForgotPasswordPage.description")}
          </Typography>

          {submitted ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("ForgotPasswordPage.successMessage")}
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                  label={t("ForgotPasswordPage.emailLabel")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting}
                  sx={{ py: 1.5 }}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("ForgotPasswordPage.sendResetLinkButton")
                  )}
                </Button>
              </Stack>
            </form>
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 3 }}
          >
            <Trans
              i18nKey="ForgotPasswordPage.rememberedItText"
              t={t}
              components={[
                <Link component={RouterLink} to="/login" variant="body2" key="link0" />,
              ]}
            />
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
